class SuperURLSearchParams extends URLSearchParams {
  get(name) {
    return new URLSearchParams(window.location.search).get(name);
  }

  keys() {
    return new URLSearchParams(window.location.search).keys();
  }

  sync() {
    window.history.replaceState({}, '', `${window.location.pathname}?${this.toString()}`);
  }
}

if (!customElements.get('fyre-product-form')) {
  class NewFyreProductForm extends HTMLElement {
    urlSearchParams = new SuperURLSearchParams(window.location.search);

    connectedCallback() {
      this.selectElements();
      this.attachListeners();
    }

    selectElements() {
      this.variantRadios = this.querySelectorAll('#pdp-variants input[name^="options"]');
      this.hiddenInput = this.querySelector('.product-variant-id');
      this.quantityInput = this.querySelector('#quantity');
      this.increaseBtn = this.querySelector('#increase');
      this.decreaseBtn = this.querySelector('#decrease');
      const tag = this.querySelector('script#data');
      this.variantGroups = this.querySelectorAll('.pdp-variant-group');
      const variantsScript = this.querySelector('#variants-data');
      this.variantsData = variantsScript ? JSON.parse(variantsScript.textContent) : [];
      this.addOns = this.querySelectorAll('#pdp-add-ons input[type="checkbox"]');
      this.addOnsPrice = this.querySelector('#add-on-total .add-on-price');
      this.passAddOnPills = this.querySelectorAll('.add-on-pill.passenger-add-on');
      this.seatChoiceRadios = this.querySelectorAll('input[name="seat_choice"]');
      // document.querySelector intentional — seat choice radio lives outside the custom element shadow
      this.isPassengerSeat = document.querySelector('input[name="seat_choice"][value="Pair of Seats"]');
      this.miniCart = document.querySelector('mini-cart');
      const pdpData = document.getElementById('pdp-data');
      this.pdpType = JSON.parse(pdpData.textContent)[0]['product-type'];
      this.addCartBtn = document.querySelector('#add-to-cart-btn');
      this.pdpBtnPrice = document.querySelector('.pdp-btn-price');

      try {
        this.data = JSON.parse(tag.textContent);
      } catch (error) {
        console.log(error);
        Sentry.captureException(error, { extra: { context: 'selectElements: failed to parse script#data tag' } });
        this.data = {};
      }

      this.addOn_total = 0;
      this.pdp_total = 0;
      this.addOnIds = [];
      this.addOnDetails = [];
    }

    attachListeners() {
      window.addEventListener('load', () => {
        this.updateOptionAvailability();
        this.updateAddOnPills();
      });

      this.variantRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          const radioGroup = radio.closest('.pdp-variant-group');
          const otherRadios = radioGroup.querySelectorAll('input[type="radio"]');
          otherRadios.forEach((otherRadio) => {
            if (otherRadio !== radio) {
              otherRadio.removeAttribute('checked');
              otherRadio.parentNode.classList.remove('active-pill-option');
            }
          });
          radio.setAttribute('checked', 'checked');
          radio.parentNode.classList.add('active-pill-option');
          Sentry.addBreadcrumb({
            category: 'atc',
            message: 'variant selected',
            data: {
              option: radio.name.match(/\[(.*?)\]/)?.[1],
              value: radio.value,
            },
            level: 'info',
          });
          this.updateVariantId();
        });
      });

      if (this.addOns.length > 0) {
        this.addOns.forEach((singleAddOn) => {
          const pill = singleAddOn.closest('.add-on-pill');

          singleAddOn.addEventListener('change', () => {
            singleAddOn.toggleAttribute('checked');

            if (singleAddOn.checked) {
              pill.classList.add('active-pill-option');
            } else {
              pill.classList.remove('active-pill-option');
            }

            Sentry.addBreadcrumb({
              category: 'atc',
              message: 'addon toggled',
              data: {
                name: singleAddOn.getAttribute('add-on-name'),
                value: singleAddOn.getAttribute('add-on-value'),
                checked: singleAddOn.checked,
              },
              level: 'info',
            });

            this.updateAddOnPrice();
          });
        });
      }

      this.seatChoiceRadios.forEach((radio) => {
        radio.addEventListener('change', (e) => {
          Sentry.addBreadcrumb({
            category: 'atc',
            message: 'seat choice changed',
            data: { value: e.target.value },
            level: 'info',
          });
          this.updateAddOnPills(e);
        });
      });

      this.increaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(this.quantityInput.value);
        this.quantityInput.value = currentValue + 1;
        Sentry.addBreadcrumb({
          category: 'atc',
          message: 'quantity changed',
          data: { from: currentValue, to: currentValue + 1 },
          level: 'info',
        });
      });
      this.decreaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(this.quantityInput.value);
        if (currentValue > 1) {
          this.quantityInput.value = currentValue - 1;
          Sentry.addBreadcrumb({
            category: 'atc',
            message: 'quantity changed',
            data: { from: currentValue, to: currentValue - 1 },
            level: 'info',
          });
        }
      });

      this.querySelector('#product-form').addEventListener('submit', (e) => this.handleFormSubmit(e));

      // Seat pair must be clicked before addons so passenger pills are enabled first (F4)
      this.handleQueryParams();

      this.initQuantityListeners();
    }

    updateQueryString = (variantId) => {
      this.urlSearchParams.set('variant', variantId);
      this.urlSearchParams.sync();
      this.updatePDP();
    };

    getAvailableCombinations() {
      return new Set(this.variantsData.map((v) => v.options.join('|')));
    }

    getSelectedOptions = () => {
      const selectedOptions = {};
      this.variantGroups.forEach((group) => {
        const selectedRadio = group.querySelector('input[type="radio"]:checked');
        if (selectedRadio) {
          const optionName = selectedRadio.name.match(/\[(.*?)\]/)[1];
          selectedOptions[optionName] = selectedRadio.value;
        }
      });
      return selectedOptions;
    };

    isOptionAvailable(optionValue, optionName) {
      this.getSelectedOptions();
      const combinations = this.getAvailableCombinations();

      const currentOptions = [];
      this.variantGroups.forEach((group) => {
        const name = group.querySelector('input[type="radio"]').name.match(/\[(.*?)\]/)[1];
        if (name === optionName) currentOptions.push(optionValue);
        else {
          const selected = group.querySelector('input[type="radio"]:checked');
          currentOptions.push(selected?.value ?? '*');
        }
      });

      const testKey = currentOptions.join('|');
      return combinations.has(testKey);
    }

    updateOptionAvailability() {
      this.variantGroups.forEach((group) => {
        const radios = group.querySelectorAll('input[type="radio"]');
        radios.forEach((radio) => {
          const optionName = radio.name.match(/\[(.*?)\]/)[1];
          const optionValue = radio.value;
          const available = this.isOptionAvailable(optionValue, optionName);
          radio.disabled = !available;

          const label = radio.parentNode;
          if (available) label.classList.remove('disabled-option');
          else label.classList.add('disabled-option');
        });
      });
    }

    updateVariantId = () => {
      const selectedOptions = {};
      let variantId = null;

      this.variantRadios.forEach((radio) => {
        if (radio.checked) {
          selectedOptions[radio.name.split('[')[1].split(']')[0]] = radio.value;
        }
      });

      this.variantsData.forEach((variantData) => {
        let match = true;

        Object.keys(selectedOptions).forEach((option) => {
          const variantOptionValue = variantData.options;
          if (Array.isArray(variantOptionValue)) {
            if (!variantOptionValue.includes(selectedOptions[option])) match = false;
          } else {
            if (selectedOptions[option] !== variantOptionValue) match = false;
          }
        });

        if (match) {
          variantId = variantData.id;
          return;
        }
      });

      this.hiddenInput.value = variantId;
      this.updateQueryString.call(this, variantId);
      this.updateOptionAvailability();
    };

    clearAddOnsAll = () => {
      if (this.addOns.length > 0) {
        this.addOns.forEach((addon) => {
          addon.checked = false;
          addon.removeAttribute('checked');
        });
        this.updateAddOnPrice();
      } else {
        this.updateBtnPrice();
      }
    };

    updateAddOnPrice = (has_passenger) => {
      if (this.addOns.length > 0) {
        this.addOn_total = 0;
        this.addOns.forEach((addon) => {
          if (addon.checked) {
            const addOnPrice = parseFloat(addon.getAttribute('add-on-price'));
            this.addOn_total += addOnPrice;
          }
        });
        this.addOnsPrice.textContent = this.addOn_total.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        if (this.isPassengerSeat) {
          const isPassengerSeatChecked = this.isPassengerSeat.checked;
          has_passenger = isPassengerSeatChecked;
        } else {
          has_passenger = false;
        }

        this.updateBtnPrice(this.addOn_total, has_passenger);
      }
    };

    updateBtnPrice = (addOn_total, has_passenger) => {
      this.pdp_total = 0;
      const the_cart_btn = document.querySelector('#add-to-cart-btn');
      let pdpBtnPrice = document.querySelector('.pdp-btn-price');
      if (!pdpBtnPrice) pdpBtnPrice = document.querySelector('.pdp-btn-price');

      const productTotal = parseFloat(the_cart_btn.dataset.productTotal.replace(/,/g, ''));

      // Reclining SKU already represents a pair — only non-reclining pair doubles the base price
      if (has_passenger && this.pdpType !== 'Reclining Seat' && this.pdpType !== 'Reclining Suspension Seat') {
        this.pdp_total = productTotal * 2;
      } else this.pdp_total = productTotal;

      let total_total;
      if (this.addOns.length > 0) total_total = addOn_total + this.pdp_total;
      else total_total = this.pdp_total;

      if (pdpBtnPrice) {
        pdpBtnPrice.innerHTML =
          '$' + total_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    };

    updateAddOnPills = () => {
      const isSingleSeatChecked = document.querySelector('input[name="seat_choice"][value="Single Seat"]')?.checked;
      this.passAddOnPills.forEach((pill) => {
        const checkbox = pill.querySelector('input[type="checkbox"]');

        if (isSingleSeatChecked) {
          pill.classList.add('disable');
          checkbox.checked = false;
          checkbox.removeAttribute('checked');
          this.quantityInput.value = 1;
          this.quantityInput.setAttribute('value', 1);
          this.updateAddOnPrice();
        } else {
          pill.classList.remove('disable');
          // Reclining types keep qty=1 because their SKU already represents a pair (B4)
          if (this.pdpType !== 'Reclining Seat' && this.pdpType !== 'Reclining Suspension Seat') {
            this.quantityInput.value = 2;
            this.quantityInput.setAttribute('value', 2);
            this.updateAddOnPrice();
          }
        }
      });
    };

    updateAddOnDetails = () => {
      this.addOnIds = [];
      this.addOnDetails = [];

      const addOns = document.querySelectorAll('#pdp-add-ons input[type="checkbox"]');

      addOns.forEach((addon) => {
        if (addon) {
          const addOnId = addon.getAttribute('add-on-id');
          const addOnName = addon.getAttribute('add-on-name');
          const addOnValue = addon.getAttribute('add-on-value');
          const addOnPrice = parseFloat(addon.getAttribute('add-on-price-flat'));
          let is_checked = false;

          is_checked = addon.checked;

          this.addOnIds.push(addOnId);
          this.addOnDetails.push({
            id: addOnId,
            name: addOnName,
            value: addOnValue,
            checked: is_checked,
            price: addOnPrice,
          });
        }
      });
    };

    getQueryParam(param) {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(param);
    }

    hasRelevantQueryParams() {
      for (const key of this.urlSearchParams.keys()) {
        if (key !== 'variant') return true;
      }
      return false;
    }

    checkSeatPair = () => {
      const seatPair = this.urlSearchParams.get('_seat_pair');
      if (seatPair === 'true') {
        const seatInput = document.querySelector('input[type="radio"][name="seat_choice"][value="Pair of Seats"]');
        seatInput?.click();
      }
      const addonIdsString = this.getQueryParam('_addon_ids');
      if (addonIdsString) this.checkAddonInputs(addonIdsString);
    };

    checkAddonInputs(addonIdsString) {
      const addonIdsArray = addonIdsString.split(',');
      addonIdsArray.forEach((addonId) => {
        const input = document.querySelector(`input[add-on-value="${addonId}"]`);
        input?.click();
      });
    }

    handleQueryParams() {
      if (this.hasRelevantQueryParams()) this.checkSeatPair();
    }

    initQuantityListeners() {
      const quantityInput = document.querySelector('input#quantity');
      const increaseBtn = document.querySelector('#increase');
      const decreaseBtn = document.querySelector('#decrease');

      quantityInput?.addEventListener('change', () => {
        this.updateBtnPrice(this.addOn_total, this.isPassengerSeat?.checked ?? false);
      });

      quantityInput?.addEventListener('input', () => {
        this.updateBtnPrice(this.addOn_total, this.isPassengerSeat?.checked ?? false);
      });

      // setTimeout defers the read until after the browser has committed the new input value
      increaseBtn?.addEventListener('click', () => {
        setTimeout(() => {
          this.updateBtnPrice(this.addOn_total, this.isPassengerSeat?.checked ?? false);
        }, 50);
      });

      decreaseBtn?.addEventListener('click', () => {
        setTimeout(() => {
          this.updateBtnPrice(this.addOn_total, this.isPassengerSeat?.checked ?? false);
        }, 50);
      });
    }

    getEffectiveMainQty = () => {
      const isReclining = this.pdpType === 'Reclining Seat' || this.pdpType === 'Reclining Suspension Seat';
      const isBench = this.pdpType === 'Bench Seat';
      const seatChoice = this.querySelector('input[name="seat_choice"]:checked')?.value;

      if (isBench) return 1;
      // Reclining SKU represents a pair; qty is always 1 even though Pair radio is pre-checked
      if (isReclining) return 1;
      if (seatChoice === 'Single Seat') return 1;
      if (seatChoice === 'Pair of Seats') return 2;
      return parseInt(this.querySelector('#quantity').value, 10) || 1;
    };

    handleFormSubmit = async (e) => {
      e.preventDefault();
      if (this._updating) return;
      // E13: double-submit guard — disabled until response settles
      if (this._submitting) return;
      this._submitting = true;
      this.addCartBtn.disabled = true;

      const hiddenInput = this.hiddenInput;
      const variantId = String(hiddenInput.value);
      if (!variantId) return;
      const uniqueKey = crypto.randomUUID();

      const addOns = document.querySelectorAll('#pdp-add-ons input[type="checkbox"]');

      if (addOns.length > 0) this.updateAddOnDetails();

      const props = {};
      const addon_ids = [];
      let addon_total_prop = 0;
      const hasSellByPair = this.isPassengerSeat != null;
      const isPair = hasSellByPair && this.isPassengerSeat.checked;

      // Group driver/passenger slots by addon name before iterating — order-independent assembly (E4, fixes #10914)
      const byName = new Map();
      for (const a of this.addOnDetails) {
        if (!byName.has(a.name)) byName.set(a.name, { driver: false, passenger: false, anyChecked: false });
        const slot = byName.get(a.name);
        if (a.checked) {
          slot.anyChecked = true;
          if (a.value.includes('passenger-')) slot.passenger = true;
          else slot.driver = true;
          addon_ids.push(a.value);
          addon_total_prop += a.price;
        }
      }

      // Every addon defined on the product emits a property whether checked or not (E5, fixes #11186)
      for (const [name, slot] of byName) {
        if (isPair) {
          if (slot.driver && slot.passenger) props[name] = 'Driver & Passenger';
          else if (slot.driver) props[name] = 'Driver';
          else if (slot.passenger) props[name] = 'Passenger';
          else props[name] = 'None';
        } else {
          props[name] = slot.anyChecked ? 'Yes' : 'None';
        }
      }

      if (hasSellByPair) {
        if (isPair) {
          props['Seat Count'] = '2';
          props['Seat'] = 'Driver & Passenger';
          props['_seat_pair'] = 'true';
        } else {
          props['Seat'] = 'Driver';
        }
      }

      // _unique_key on every main item, even with no addons — required for grouped cart remove/edit (E3, G2)
      props['_unique_key'] = uniqueKey;
      // Comma-string (not array) — matches F3 parse format and survives Shopify property storage (E12)
      if (addon_ids.length) props['_addon_ids'] = addon_ids.join(',');
      if (addon_ids.length) props['_addons_total'] = addon_total_prop;

      const mainQty = this.getEffectiveMainQty();
      const pdpItem = {
        id: variantId,
        quantity: mainQty,
        properties: props,
      };

      // Pair mode: each checkbox emits qty=1; Shopify merges identical variant+properties into one line (E10, E11)
      // _hide_in_cart keeps addon lines off cart display; totals are read from _addons_total on the main item (G1)
      const addOnItems = this.addOnDetails
        .filter((d) => d.checked)
        .map((a) => ({
          id: a.id,
          quantity: isPair ? 1 : mainQty,
          properties: { _unique_key: uniqueKey, _hide_in_cart: true },
        }));

      const items = [pdpItem].concat(addOnItems);

      console.log('[ATC] line item properties', props);

      const formData = {
        items: items,
        sections: 'fyre-mini-cart',
        sections_url: window.location.pathname + window.location.search,
      };

      const addCartBtn = this.addCartBtn;
      const currentCartBtnHTML = addCartBtn.innerHTML;

      Sentry.logger.info('atc:form-submit', {
        uniqueKey,
        variantId,
        pdpType: this.pdpType,
        isPair,
        hasSellByPair,
        mainQty,
        addonIds: addon_ids,
        addonTotal: addon_total_prop,
        addonDetails: this.addOnDetails,
        addonSlotsByName: Object.fromEntries(byName),
        lineItemProperties: props,
        items,
      });

      try {
        const json = await CartApi.add(formData);
        if (!json) {
          addCartBtn.innerHTML = currentCartBtnHTML;
          throw new Error('No response from cart API');
        }
        const { sections: _, ...cartResponse } = json;
        Sentry.logger.info('atc:cart-api-success', { uniqueKey, ...cartResponse });
        addCartBtn.innerHTML = 'Added to Cart';
        this.miniCart.querySelector('div.product').innerHTML = new DOMParser()
          .parseFromString(json.sections['fyre-mini-cart'], 'text/html')
          .querySelector('div.product')?.innerHTML;
        setTimeout(() => {
          addCartBtn.innerHTML = currentCartBtnHTML;
        }, 2000);
        this.miniCart?.open({ product: json.items[0] });
      } catch (error) {
        Sentry.captureException(error, { extra: { uniqueKey, variantId, items } });
        console.error('Failed to submit atc form', error);
        addCartBtn.innerHTML = currentCartBtnHTML;
      }
      this._submitting = false;
      this.addCartBtn.disabled = false;
    };

    async updatePDP() {
      this._updating = true;
      document.querySelector('#add-to-cart-btn')?.setAttribute('disabled', '');

      try {
        const sectionId = 'fyre-pdp-main';
        const res = await SectionRenderingApi.fetchSections({ ids: [sectionId] });
        if (!res) return;

        const sectionElement = res[sectionId];
        if (!sectionElement) return;
        const updatedContent = sectionElement.querySelector('.pdp-info-update');
        const updatedFormContents = sectionElement.querySelectorAll('.pdp-form-update');
        const pdpInfoUpdate = document.querySelector('.pdp-info-update');

        if (pdpInfoUpdate && updatedContent) pdpInfoUpdate.innerHTML = updatedContent.innerHTML;

        const currentFormContents = document.querySelectorAll('.pdp-form-update');
        updatedFormContents.forEach((updatedFormContent, index) => {
          if (currentFormContents[index]) currentFormContents[index].innerHTML = updatedFormContent.innerHTML;
        });
        // The button was replaced by the DOM swap above — re-disable the new one
        document.querySelector('#add-to-cart-btn')?.setAttribute('disabled', '');

        if (this.addOns.length > 0) this.clearAddOnsAll();

        const imgMsg = document.querySelector('#main-pdp').getAttribute('data-img-msg');
        const toolTip = document.querySelector('#tooltip-pdp .tooltip-content');
        const imgMsgBtm = document.querySelector('#img-msg-btm .img-msg');

        if (imgMsg && toolTip) toolTip.innerHTML = imgMsg;
        if (imgMsg && imgMsgBtm) imgMsgBtm.innerHTML = imgMsg;
      } finally {
        this._updating = false;
        document.querySelector('#add-to-cart-btn')?.removeAttribute('disabled');
      }
    }

    disconnectedCallback() {}
  }

  customElements.define('fyre-product-form', NewFyreProductForm);
}
